/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-unsafe-optional-chaining */
import { displayPhoneNumber } from '@brands/Dashboard/MyProfile/Patient/MyProfile';
import { ICase } from '@brands/services/cases/types/ICase';
import { addOneReportObject } from '@brands/services/oneReport/addOneReportObject';
import { deleteOneReportObject } from '@brands/services/oneReport/deleteOneReportObject';
import { IPrescription, PrescriptionPayload } from '@brands/services/oneReport/types/ITreatments';
import { ObjectType, updateOneReportObject } from '@brands/services/oneReport/updateOneReportObject';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Menu, MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import dayjs from 'dayjs';
import { parsePhoneNumber } from 'libphonenumber-js';
import React, { useEffect, useState } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { AiFillPlusCircle } from 'react-icons/ai';
import { IoTrashOutline } from 'react-icons/io5';

import TextArea from '../../../../Components/Inputs/TextArea/TextArea';
import { useWindowSize } from '../../../../hooks';
import { UserProfile } from '../../../../services/identity/types/UserProfile';
import { IOneReport } from '../../../../services/oneReport/types/IOneReport';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { checkCollapsibleHeaderColor } from '../../../../Utils/getCollapsibleHeader';
import { OralHealthReportSubmit, Prescription } from '../../assets/types';
import styles from '../../styles.module.scss';
import Pill from '../PossibleTreatmentNeedsSection/icons/pill.svg';
import PrescriptionModal, { PrescriptionForm } from './modal/PrescriptionModal';
import ConfirmActionModal from '../../Modals/ConfirmActionModal/ConfirmActionModal';

type PrescriptionSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  userInfo: UserProfile;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
};

export const convertPrescriptionType = (iPrescription: IPrescription[]): Prescription[] => {
  return iPrescription.map((prescription) => ({
    drug: prescription.drug ? prescription.drug.charAt(0).toUpperCase() + prescription.drug.slice(1) : '',
    sig: prescription.sig ? prescription.sig.charAt(0).toUpperCase() + prescription.sig.slice(1) : '',
    prescription_date: prescription.prescription_date || '',
    created_at: prescription.created_at || '',
    dispense: prescription.dispense
      ? prescription.dispense.charAt(0).toUpperCase() + prescription.dispense.slice(1)
      : '',
    refills: prescription.refills ? prescription.refills.charAt(0).toUpperCase() + prescription.refills.slice(1) : '',
    pharmacy: {
      pharmacy_name: prescription.pharmacy?.pharmacy_name || '',
      pharmacy_street_address: prescription.pharmacy?.pharmacy_street_address || '',
      pharmacy_city: prescription.pharmacy?.pharmacy_city || '',
      pharmacy_state: prescription.pharmacy?.pharmacy_state || '',
      pharmacy_zip_code: prescription.pharmacy?.pharmacy_zip_code || '',
      pharmacy_phone_number: prescription.pharmacy?.pharmacy_phone_number || '',
    },
    id: prescription.id,
  }));
};

const PrescriptionSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  register,
  setValue,
  trigger,
}: PrescriptionSectionProps): JSX.Element => {
  const screenSize = useWindowSize();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>(
    convertPrescriptionType(thisCase?.one_report?.prescriptions || []),
  );
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingPrescription, setEditingPrescription] = useState<PrescriptionForm | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activePrescriptionIndex, setActivePrescriptionIndex] = useState<number | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingPrescription, setIsDeletingPrescription] = useState(false);

  useEffect(() => {
    setPrescriptions(convertPrescriptionType(thisCase?.one_report?.prescriptions || []));
  }, [thisCase?.one_report?.prescriptions]);

  const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement>, index: number) => {
    setMenuAnchorEl(event.currentTarget);
    setActivePrescriptionIndex(index);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // setActivePrescriptionIndex(null);
  };

  const handleEditPrescription = () => {
    if (activePrescriptionIndex === null) return;

    const prescription = prescriptions[activePrescriptionIndex];

    setEditingIndex(activePrescriptionIndex);
    setEditingPrescription({
      drug: prescription.drug || '',
      sig: prescription.sig || '',
      prescription_date: prescription.prescription_date || '',
      dispense: prescription.dispense || '',
      refills: prescription.refills || '',
      pharmacy_name: prescription.pharmacy?.pharmacy_name || '',
      pharmacy_street_address: prescription.pharmacy?.pharmacy_street_address || '',
      pharmacy_city: prescription.pharmacy?.pharmacy_city || '',
      pharmacy_state: prescription.pharmacy?.pharmacy_state || '',
      pharmacy_zip_code: prescription.pharmacy?.pharmacy_zip_code || '',
      pharmacy_phone_number: prescription.pharmacy?.pharmacy_phone_number || '',
    });

    handleMenuClose();
    setIsPrescriptionModalOpen(true);
  };

  const syncPrescriptionState = (oneReport: Partial<IOneReport>): void => {
    const rawPrescriptions = (oneReport?.prescriptions ?? []) as IPrescription[];
    const convertedPrescriptions = convertPrescriptionType(rawPrescriptions);

    setPrescriptions(convertedPrescriptions);

    setConsultationForm((prev) => ({
      ...prev,
      prescriptions: convertedPrescriptions,
      ...(oneReport?.pharmacies !== undefined ? { pharmacies: oneReport.pharmacies } : {}),
    }));

    setCase((prev) => ({
      ...prev,
      one_report: {
        ...prev.one_report,
        ...oneReport,
        prescriptions: rawPrescriptions,
      },
    }));
  };

  const addEmptyPrescriptionRow = async (): Promise<void> => {
    try {
      const prescriptionResponse = await addOneReportObject(thisCase?.one_report.id || '', ObjectType.Prescription, {
        drug: '',
        sig: '',
        prescription_date: '',
        dispense: '',
        refills: '',
      });

      let finalResponse = prescriptionResponse;

      if (prescriptionResponse.pharmacies === null) {
        const pharmacyResponse = await addOneReportObject(thisCase?.one_report.id || '', ObjectType.Pharmacy, {
          pharmacy_name: '',
          pharmacy_street_address: '',
          pharmacy_city: '',
          pharmacy_state: '',
          pharmacy_zip_code: '',
          pharmacy_phone_number: '',
        });

        finalResponse = {
          ...prescriptionResponse,
          pharmacies: pharmacyResponse.pharmacies,
        };
      }

      syncPrescriptionState(finalResponse);
    } catch (error) {
      displayErrorDetails(`Error adding new prescription or pharmacy: ${error}`);
    }
  };

  const addPrescription = async (payload: IPrescription): Promise<IOneReport | null> => {
    try {
      const res = await addOneReportObject(thisCase?.one_report.id || '', ObjectType.Prescription, payload);
      syncPrescriptionState(res);
      return res;
    } catch (error) {
      displayErrorDetails(error);
      return null;
    }
  };

  const updatePrescription = async (id: string, payload: PrescriptionPayload): Promise<IOneReport | null> => {
    try {
      const res = await updateOneReportObject(thisCase?.one_report.id || '', ObjectType.Prescription, {
        ...payload,
        id,
      });
      syncPrescriptionState(res);
      return res;
    } catch (error) {
      displayErrorDetails(error);
      return null;
    }
  };

  const deletePrescription = async (index: number, id?: string): Promise<void> => {
    const oneReportId = thisCase?.one_report?.id;
    const prescriptionExists = thisCase?.one_report?.prescriptions?.some((pres) => pres.id === id);

    try {
      if (!id || !oneReportId || !prescriptionExists) {
        setPrescriptions((prev) => prev.filter((_, i) => i !== index));
        setConsultationForm((prev) => ({
          ...prev,
          prescriptions: prev.prescriptions.filter((_, i) => i !== index),
        }));
        return;
      }

      const res = await deleteOneReportObject(oneReportId, id, ObjectType.Prescription);

      if ((res?.prescriptions ?? []).length === 0) {
        setValue('prescriptions', []);
        trigger('prescriptions');

        if (res?.pharmacies?.id) {
          const pharmacyRes = await deleteOneReportObject(
            oneReportId,
            res.pharmacies.id as string,
            ObjectType.Pharmacy,
          );
          syncPrescriptionState({
            ...res,
            pharmacies: pharmacyRes?.pharmacies,
            prescriptions: [],
          });
          return;
        }
      }

      syncPrescriptionState(res);
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  const handleDeletePrescription = () => {
    if (activePrescriptionIndex === null) return;

    setIsDeleteModalOpen(true);
    handleMenuClose();
  };

  const confirmDeletePrescription = async () => {
    if (activePrescriptionIndex === null) return;

    const prescription = prescriptions[activePrescriptionIndex];

    try {
      setIsDeletingPrescription(true);
      await deletePrescription(activePrescriptionIndex, prescription.id);
      setIsDeleteModalOpen(false);
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setIsDeletingPrescription(false);
    }
  };

  const handleAddPrescription = () => {
    setEditingIndex(null);
    setEditingPrescription(null);
    setIsPrescriptionModalOpen(true);
  };

  const mapPrescriptionFormToPayload = (data: PrescriptionForm): PrescriptionPayload => ({
    drug: data.drug,
    sig: data.sig,
    prescription_date: data.prescription_date,
    dispense: data.dispense,
    refills: data.refills,
    pharmacy: {
      pharmacy_name: data.pharmacy_name,
      pharmacy_street_address: data.pharmacy_street_address,
      pharmacy_city: data.pharmacy_city,
      pharmacy_state: data.pharmacy_state,
      pharmacy_zip_code: data.pharmacy_zip_code,
      pharmacy_phone_number: data.pharmacy_phone_number,
    },
  });

  const handleSavePrescription = async (data: PrescriptionForm): Promise<void> => {
    try {
      const payload = mapPrescriptionFormToPayload(data);

      if (editingIndex !== null) {
        const prescription = prescriptions[editingIndex];

        if (!prescription?.id) {
          return;
        }

        await updatePrescription(prescription.id, payload);
      } else {
        await addPrescription(payload as IPrescription);
      }

      setEditingIndex(null);
      setEditingPrescription(null);
      setIsPrescriptionModalOpen(false);
    } catch (error) {
      displayErrorDetails(error);
    }
  };

  return (
    <div
      className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
        prescriptions.length > 0 ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
      } `}
    >
      <div className={styles.possibleTreatmentNeedsSectionDivChildRow}>
        <span className={styles.possibleTreatmentNeedsSectionDivChildPrescription}>
          <img src={Pill} />
        </span>
        {prescriptions.length === 0 ? (
          <Button
            variant="text"
            onClick={handleAddPrescription}
            disabled={checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') !== 'edit'}
          >
            Add prescription
          </Button>
        ) : (
          <div className={styles.existDataDiv}>
            <span>Prescriptions</span>
            <Button
              variant="text"
              onClick={handleAddPrescription}
              disabled={checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') !== 'edit'}
            >
              <AddIcon /> Add
            </Button>
          </div>
        )}
      </div>

      {prescriptions.length > 0 && (
        <div className={styles.prescriptionBody}>
          {prescriptions.map((prescription, index) => {
            return (
              <React.Fragment key={prescription.id || index}>
                <Divider orientation="horizontal" flexItem className={styles.divider} />

                <div className={styles.prescriptionDetails}>
                  <div className={styles.prescriptionDetailsDrugRow}>
                    <span className={styles.prescriptionDetailsDrug}>{prescription.drug}</span>

                    <IconButton
                      className={styles.moreIcon}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleMenuOpen(e, index)}
                      disabled={checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') !== 'edit'}
                      size="small"
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </div>

                  <div className={styles.prescriptionDetailBlock}>
                    <span className={styles.prescriptionDetailBlockHeader}>Directions</span>
                    <span className={styles.prescriptionDetailBlockValue}>{prescription.sig}</span>
                  </div>

                  <div className={styles.prescriptionDetailBlock}>
                    <span className={styles.prescriptionDetailBlockHeader}>Dispense</span>
                    <span className={styles.prescriptionDetailBlockValue}>{prescription.dispense}</span>
                  </div>

                  <div className={styles.prescriptionDetailBlock}>
                    <span className={styles.prescriptionDetailBlockHeader}>Refills</span>
                    <span className={styles.prescriptionDetailBlockValue}>{prescription.refills}</span>
                  </div>

                  <div className={styles.prescriptionDetailBlock}>
                    <span className={styles.prescriptionDetailBlockHeader}>Pharmacy</span>
                    <span className={styles.prescriptionDetailBlockValue}>{prescription.pharmacy?.pharmacy_name}</span>
                    <span className={styles.prescriptionDetailBlockValue}>
                      {prescription.pharmacy?.pharmacy_street_address}
                    </span>
                    <span
                      className={styles.prescriptionDetailBlockValue}
                    >{`${prescription.pharmacy?.pharmacy_city}, ${prescription.pharmacy?.pharmacy_state} ${prescription.pharmacy?.pharmacy_zip_code}`}</span>
                    <span className={styles.prescriptionDetailBlockValue}>{`Phone: ${
                      prescription?.pharmacy?.pharmacy_phone_number
                        ? parsePhoneNumber(prescription?.pharmacy?.pharmacy_phone_number, 'US')?.formatNational()
                        : ''
                    }`}</span>
                  </div>

                  <div className={styles.prescriptionDetailBlock}>
                    <span className={styles.prescriptionDetailBlockHeader}>Prescription date</span>
                    <span className={styles.prescriptionDetailBlockValue}>
                      {prescription.prescription_date ? dayjs(prescription.prescription_date).format('MM/DD/YYYY') : ''}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditPrescription}>Edit</MenuItem>
        <MenuItem onClick={handleDeletePrescription} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      <PrescriptionModal
        open={isPrescriptionModalOpen}
        onClose={() => {
          setEditingIndex(null);
          setEditingPrescription(null);
          setIsPrescriptionModalOpen(false);
        }}
        onSave={handleSavePrescription}
        initialData={editingPrescription}
        editingIndex={editingIndex}
        consultationForm={consultationForm}
        setConsultationForm={setConsultationForm}
        thisCase={thisCase}
        setCase={setCase}
      />

      <ConfirmActionModal
        open={isDeleteModalOpen}
        title="Delete prescription"
        message={`Are you sure you want to delete ${
          activePrescriptionIndex !== null
            ? prescriptions[activePrescriptionIndex]?.drug || 'this prescription'
            : 'this prescription'
        }?`}
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        isLoading={isDeletingPrescription}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeletePrescription}
      />
    </div>
  );
};

export default React.memo(PrescriptionSection);
